import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Toast from '../components/Toast';
import FilterableTable from '../components/FilterableTable';
import { filtreazaCantitate, normalizeazaNumar, formatCantitate } from '../utils/format';

export default function Produse() {
  const navigate = useNavigate();
  const [produse, setProduse] = useState([]);
  const [materiale, setMateriale] = useState([]);
  const [magazii, setMagazii] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ denumire: '', um: 'buc', tinePeStoc: true, pragAlerta: 0, pretVanzareEstimat: '' });
  const [retetaModal, setRetetaModal] = useState(null);
  const [reteta, setReteta] = useState([]);
  const [componentaNoua, setComponentaNoua] = useState({ materialId: '', cantitate: '' });
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  function incarca() {
    setLoading(true);
    Promise.all([api.get('/api/produse'), api.get('/api/materiale'), api.get('/api/magazii')])
      .then(([p, m, g]) => { setProduse(p.data); setMateriale(m.data); setMagazii(g.data); })
      .finally(() => setLoading(false));
  }

  useEffect(incarca, []);

  function deschideNou() {
    setEditId(null);
    setForm({ denumire: '', um: 'buc', tinePeStoc: true, pragAlerta: 0, pretVanzareEstimat: '' });
    setShowModal(true);
  }

  function deschideEditare(p) {
    setEditId(p.id);
    setForm({ denumire: p.denumire, um: p.um, tinePeStoc: !!p.tinePeStoc, pragAlerta: p.pragAlerta, pretVanzareEstimat: p.pretVanzareEstimat ?? '' });
    setShowModal(true);
  }

  async function salveaza(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        pragAlerta: normalizeazaNumar(form.pragAlerta) || 0,
        pretVanzareEstimat: form.pretVanzareEstimat === '' ? null : normalizeazaNumar(form.pretVanzareEstimat)
      };
      if (editId) await api.put(`/api/produse/${editId}`, payload);
      else await api.post('/api/produse', payload);
      setToast({ message: 'Salvat cu succes', type: 'ok' });
      setShowModal(false);
      incarca();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la salvare', type: 'error' });
    }
  }

  async function dezactiveaza(id) {
    if (!window.confirm('Dezactivezi acest produs?')) return;
    await api.delete(`/api/produse/${id}`);
    incarca();
  }

  async function deschideReteta(p) {
    setRetetaModal(p);
    const res = await api.get(`/api/produse/${p.id}/reteta`);
    setReteta(res.data);
    setComponentaNoua({ materialId: '', cantitate: '' });
  }

  async function adaugaComponenta(e) {
    e.preventDefault();
    if (!componentaNoua.materialId || !componentaNoua.cantitate) return;
    await api.post(`/api/produse/${retetaModal.id}/reteta`, {
      ...componentaNoua, cantitate: normalizeazaNumar(componentaNoua.cantitate)
    });
    const res = await api.get(`/api/produse/${retetaModal.id}/reteta`);
    setReteta(res.data);
    setComponentaNoua({ materialId: '', cantitate: '' });
  }

  async function stergeComponenta(componentaId) {
    await api.delete(`/api/produse/${retetaModal.id}/reteta/${componentaId}`);
    const res = await api.get(`/api/produse/${retetaModal.id}/reteta`);
    setReteta(res.data);
  }

  // "Da in productie", direct din nomenclator: creeaza o comanda noua, cu o
  // singura linie (acest produs), apoi te duce direct in Comenzi, cu comanda
  // deschisa - de acolo continui normal, material cu material, din acordeon.
  const [productieModal, setProductieModal] = useState(null); // produsul pentru care se creeaza comanda
  const [productieForm, setProductieForm] = useState({ client: '', cantitate: '1', magazieId: '' });
  const [submittingProductie, setSubmittingProductie] = useState(false);

  function deschideProductie(p) {
    setProductieModal(p);
    setProductieForm({ client: '', cantitate: '1', magazieId: '' });
  }

  async function confirmaProductie(e) {
    e.preventDefault();
    if (submittingProductie) return;
    if (!productieForm.client.trim()) {
      setToast({ message: 'Numele clientului este obligatoriu', type: 'error' });
      return;
    }
    if (!productieForm.magazieId) {
      setToast({ message: 'Alege magazia de produse finite', type: 'error' });
      return;
    }
    const cantitateNormalizata = normalizeazaNumar(productieForm.cantitate);
    if (!cantitateNormalizata || Number(cantitateNormalizata) <= 0) {
      setToast({ message: 'Introdu o cantitate valida', type: 'error' });
      return;
    }
    setSubmittingProductie(true);
    try {
      const res = await api.post('/api/comenzi', {
        client: productieForm.client.trim(),
        linii: [{ itemTip: 'PRODUS_FINIT', itemId: productieModal.id, cantitate: cantitateNormalizata, magazieId: productieForm.magazieId }]
      });
      setProductieModal(null);
      navigate('/comenzi', { state: { deschideComandaId: res.data.comandaId } });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la crearea comenzii', type: 'error' });
    } finally {
      setSubmittingProductie(false);
    }
  }

  const coloane = [
    { key: 'cod', label: 'Cod' },
    { key: 'denumire', label: 'Denumire' },
    { key: 'um', label: 'UM' },
    { key: 'tinePeStoc', label: 'Tine pe stoc', render: p => p.tinePeStoc ? 'Da' : 'Nu - iese direct la client' },
    { key: 'pretVanzareEstimat', label: 'Pret vanzare estimat', render: p => p.pretVanzareEstimat != null ? `${formatCantitate(p.pretVanzareEstimat)} lei` : <em>-</em> },
    { key: 'actiuni', label: '', filterable: false, render: p => (
      <>
        <button className="btn-edit" onClick={() => deschideProductie(p)}>Da in productie</button>{' '}
        <button className="btn-edit" onClick={() => deschideReteta(p)}>Reteta (BOM)</button>{' '}
        <button className="btn-edit" onClick={() => deschideEditare(p)}>Editeaza</button>{' '}
        <button className="btn-delete" onClick={() => dezactiveaza(p.id)}>Dezactiveaza</button>
      </>
    )}
  ];

  return (
    <div>
      <h1>Nomenclator produse finite</h1>
      <div className="toolbar">
        <button className="btn-add" onClick={deschideNou}>+ Produs nou</button>
      </div>

      {loading ? <p>Se incarca...</p> : <FilterableTable columns={coloane} rows={produse} />}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editId ? 'Editeaza produs' : 'Produs nou'}</h2>
              <button type="button" className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={salveaza}>
              <div className="modal-body">
                {editId && (
                  <p style={{ fontSize: 13, color: '#888', marginTop: 0 }}>Cod: <strong>{produse.find(p => p.id === editId)?.cod}</strong></p>
                )}
                <div className="field-row">
                  <div className="field">
                    <label>Denumire *</label>
                    <input value={form.denumire} onChange={e => setForm({ ...form, denumire: e.target.value })} required autoFocus />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Unitate de masura</label>
                    <input value={form.um} onChange={e => setForm({ ...form, um: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Prag alerta (0 = fara alerta)</label>
                    <input type="text" inputMode="decimal" value={form.pragAlerta}
                      onChange={e => setForm({ ...form, pragAlerta: filtreazaCantitate(e.target.value) })} />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Pret vanzare estimat</label>
                    <input type="text" inputMode="decimal" value={form.pretVanzareEstimat}
                      onChange={e => setForm({ ...form, pretVanzareEstimat: filtreazaCantitate(e.target.value) })} />
                  </div>
                </div>
                <div className="field">
                  <label>
                    <input type="checkbox" checked={form.tinePeStoc}
                      onChange={e => setForm({ ...form, tinePeStoc: e.target.checked })} style={{ width: 'auto', marginRight: 8 }} />
                    Intra pe stoc dupa productie (altfel iese direct la client)
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setShowModal(false)}>Anuleaza</button>
                <button type="submit" className="btn">Salveaza</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {retetaModal && (
        <div className="modal-overlay" onClick={() => setRetetaModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reteta - {retetaModal.denumire}</h2>
              <button type="button" className="modal-close" onClick={() => setRetetaModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <table>
                <thead><tr><th>Material</th><th>Cantitate</th><th></th></tr></thead>
                <tbody>
                  {reteta.map(c => (
                    <tr key={c.id}>
                      <td>{c.materialDenumire}</td>
                      <td>{formatCantitate(c.cantitate)} {c.um}</td>
                      <td><button className="btn-delete" onClick={() => stergeComponenta(c.id)}>Sterge</button></td>
                    </tr>
                  ))}
                  {reteta.length === 0 && <tr><td colSpan="3">Nicio componenta inca.</td></tr>}
                </tbody>
              </table>
              <form onSubmit={adaugaComponenta} style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <select value={componentaNoua.materialId}
                  onChange={e => setComponentaNoua({ ...componentaNoua, materialId: e.target.value })} required>
                  <option value="">Alege material...</option>
                  {materiale.map(m => <option key={m.id} value={m.id}>{m.denumire} ({m.um})</option>)}
                </select>
                <input type="text" inputMode="decimal" placeholder="Cantitate" style={{ width: 100 }}
                  value={componentaNoua.cantitate}
                  onChange={e => setComponentaNoua({ ...componentaNoua, cantitate: filtreazaCantitate(e.target.value) })} required />
                <button type="submit" className="btn-add">Adauga</button>
              </form>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn secondary" onClick={() => setRetetaModal(null)}>Inchide</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />

      {productieModal && (
        <div className="modal-overlay" onClick={() => setProductieModal(null)}>
          <div className="modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Da in productie</h2>
              <button type="button" className="modal-close" onClick={() => setProductieModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaProductie}>
              <div className="modal-body">
                <p style={{ marginTop: 0, color: '#888', fontSize: 13 }}>
                  Creeaza o comanda noua cu acest produs, apoi te duce direct in Comenzi ca sa dai materialele in productie.
                </p>
                <div className="field-row">
                  <div className="field">
                    <label>Client *</label>
                    <input value={productieForm.client} onChange={e => setProductieForm({ ...productieForm, client: e.target.value })} required autoFocus />
                  </div>
                  <div className="field">
                    <label>Cantitate *</label>
                    <input type="text" inputMode="decimal" value={productieForm.cantitate}
                      onChange={e => setProductieForm({ ...productieForm, cantitate: filtreazaCantitate(e.target.value) })} required />
                  </div>
                </div>
                <div className="field">
                  <label>Magazie produse finite *</label>
                  <select value={productieForm.magazieId} onChange={e => setProductieForm({ ...productieForm, magazieId: e.target.value })} required>
                    <option value="">Alege...</option>
                    {magazii.filter(g => g.tip === 'PRODUSE_FINITE').map(g => <option key={g.id} value={g.id}>{g.cod} - {g.nume}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setProductieModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submittingProductie}>{submittingProductie ? 'Se creeaza...' : 'Creeaza comanda'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
